import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import Hero from "@/components/Hero";
import CardDraw from "@/components/CardDraw";
import Results from "@/components/Results";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SoulAnalysis() {
  const [phase, setPhase] = useState("intro"); // intro | drawing | result
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const handleSubmit = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/analyze`, payload);
      setData(res.data);
      setPhase("drawing");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Die Analyse konnte nicht erstellt werden. Bitte versuche es erneut.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReveal = () => {
    setPhase("result");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRestart = () => {
    setData(null);
    setPhase("intro");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="relative min-h-screen">
      <header className="relative z-20 w-full flex items-center justify-center py-3 px-4 border-b border-white/10 glass">
        <a
          href="https://samantha-hellsehen-kartenlegen.de"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="site-link"
          className="inline-flex items-center gap-2 text-sm tracking-wide text-white/70 hover:text-[#D4AF37] transition-colors duration-300"
        >
          <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--gold)" }} />
          <span className="font-display text-base">samantha‑hellsehen‑kartenlegen.de</span>
        </a>
      </header>
      <AnimatePresence mode="wait">
        {phase === "intro" && (
          <motion.div key="intro" exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <Hero onSubmit={handleSubmit} loading={loading} />
          </motion.div>
        )}
        {phase === "drawing" && data && (
          <motion.div key="drawing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <CardDraw cards={data.cards} name={data.name} onReveal={handleReveal} />
          </motion.div>
        )}
        {phase === "result" && data && (
          <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Results data={data} onRestart={handleRestart} />
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="relative z-20 border-t border-white/10 glass py-8 px-4">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-3 text-center">
          <a
            href="https://app.emergent.sh"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="emergent-footer-link"
            className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#D4AF37]/30 bg-white/5 hover:bg-[#D4AF37] transition-all duration-300"
          >
            <Sparkles className="w-4 h-4 text-[#D4AF37] group-hover:text-[#090514] transition-colors" />
            <span className="text-sm tracking-wide text-white/85 group-hover:text-[#090514] transition-colors">
              Erstellt mit <span className="font-display text-base font-medium">Emergent</span> – baue deine eigene App
            </span>
          </a>
          <p className="text-xs text-white/35 tracking-wide">
            © {new Date().getFullYear()} samantha‑hellsehen‑kartenlegen.de · Powered by Emergent
          </p>
        </div>
      </footer>
    </main>
  );
}
